import { useState, useRef } from "react";

// Número WhatsApp no formato internacional (sem +, espaços ou traços)
const WHATSAPP_NUMBER = "5554996914900";

// Opções do select "Área do Caso"
const areasDoCaso = [
  "Direito Bancário / Revisão de Juros",
  "Direito Trabalhista",
  "Direito Civil",
  "Direito Previdenciário",
  "Direito de Família",
  "Outro",
];

export default function Contato() {
  const [submitted, setSubmitted] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const formRef = useRef<HTMLFormElement>(null);

  /**
   * Ao enviar: captura os campos, monta a mensagem formatada,
   * codifica com encodeURIComponent e abre o WhatsApp em nova aba.
   * Nenhuma requisição de backend ou API é realizada.
   */
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    // Impede recarregamento/envio padrão do formulário
    e.preventDefault();

    const form = e.currentTarget;

    // Leitura dos valores de cada campo
    const nome = (form.elements.namedItem("nome") as HTMLInputElement).value.trim();
    const email = (form.elements.namedItem("email") as HTMLInputElement).value.trim();
    const areaDoCaso = (form.elements.namedItem("area_do_caso") as HTMLSelectElement).value;
    const mensagem = (form.elements.namedItem("mensagem") as HTMLTextAreaElement).value.trim();

    // Valida limite de caracteres da mensagem
    if (mensagem.length > 500) return;

    // Monta o texto com a estrutura exata solicitada
    const textoMontado =
      `Nome Completo: ${nome} | ` +
      `E-mail: ${email} | ` +
      `Área do Caso: ${areaDoCaso} | ` +
      `Motivo do Contato: ${mensagem}`;

    // Codifica para URL segura
    const textoCodificado = encodeURIComponent(textoMontado);

    // Abre WhatsApp Web ou app em nova aba com a mensagem pré-preenchida
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${textoCodificado}`, "_blank");

    // Mostra tela de confirmação
    setSubmitted(true);
  };

  return (
    <section id="contato" className="py-24 bg-[#F9F9F9]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Cabeçalho da seção */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-[#B32025] text-xs font-semibold uppercase tracking-widest font-sans">
            Fale Conosco
          </span>
          <h2 className="font-serif text-[#1A1A1A] text-3xl lg:text-4xl font-bold mt-3 mb-5">
            Entre em Contato
          </h2>
          <p className="text-[#2C2C2C]/70 text-base leading-relaxed font-sans">
            Preencha o formulário e clique em enviar — abriremos o WhatsApp com sua mensagem já pronta.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
          {/* Formulário */}
          <div className="lg:col-span-3 bg-white rounded-lg border border-[#E6E6E6] p-8">
            {submitted ? (
              <div className="flex flex-col items-center justify-center h-full py-16 text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-green-50 rounded-full mb-5">
                  <i className="ri-check-double-line text-3xl text-green-600 leading-none" />
                </div>
                <h3 className="font-serif text-[#1A1A1A] text-2xl font-bold mb-3">
                  WhatsApp aberto!
                </h3>
                <p className="text-[#2C2C2C]/70 text-sm font-sans max-w-sm">
                  Sua mensagem foi preparada e o WhatsApp foi aberto em uma nova aba. Basta enviar!
                </p>
                <button
                  onClick={() => {
                    setSubmitted(false);
                    formRef.current?.reset();
                    setCharCount(0);
                  }}
                  className="mt-6 text-[#B32025] text-sm font-semibold font-sans hover:underline cursor-pointer"
                >
                  Enviar outra mensagem
                </button>
              </div>
            ) : (
              <form ref={formRef} onSubmit={handleSubmit} className="space-y-5">
                {/* Campo: Nome Completo */}
                <div>
                  <label className="block text-[#1A1A1A] text-sm font-semibold font-sans mb-2">
                    Nome Completo <span className="text-[#B32025]">*</span>
                  </label>
                  <input
                    type="text"
                    name="nome"
                    required
                    placeholder="Seu nome completo"
                    className="w-full border border-[#E6E6E6] rounded-md px-4 py-3 text-sm font-sans text-[#1A1A1A] focus:outline-none focus:border-[#B32025] transition-colors"
                  />
                </div>

                {/* Campo: E-mail */}
                <div>
                  <label className="block text-[#1A1A1A] text-sm font-semibold font-sans mb-2">
                    E-mail <span className="text-[#B32025]">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="seu@email.com"
                    className="w-full border border-[#E6E6E6] rounded-md px-4 py-3 text-sm font-sans text-[#1A1A1A] focus:outline-none focus:border-[#B32025] transition-colors"
                  />
                </div>

                {/* Campo: Área do Caso (select obrigatório) */}
                <div>
                  <label className="block text-[#1A1A1A] text-sm font-semibold font-sans mb-2">
                    Área do Caso <span className="text-[#B32025]">*</span>
                  </label>
                  <select
                    name="area_do_caso"
                    required
                    defaultValue=""
                    className="w-full border border-[#E6E6E6] rounded-md px-4 py-3 text-sm font-sans text-[#1A1A1A] focus:outline-none focus:border-[#B32025] transition-colors bg-white cursor-pointer"
                  >
                    <option value="" disabled>Selecione a área do caso</option>
                    {areasDoCaso.map((area) => (
                      <option key={area} value={area}>{area}</option>
                    ))}
                  </select>
                </div>

                {/* Campo: Mensagem */}
                <div>
                  <label className="block text-[#1A1A1A] text-sm font-semibold font-sans mb-2">
                    Mensagem <span className="text-[#B32025]">*</span>
                  </label>
                  <textarea
                    name="mensagem"
                    rows={5}
                    required
                    maxLength={500}
                    placeholder="Descreva brevemente seu caso ou dúvida jurídica..."
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full border border-[#E6E6E6] rounded-md px-4 py-3 text-sm font-sans text-[#1A1A1A] focus:outline-none focus:border-[#B32025] transition-colors resize-none"
                  />
                  <p className={`text-xs font-sans mt-1 text-right ${charCount > 480 ? "text-[#B32025]" : "text-[#BFBFBF]"}`}>
                    {charCount}/500
                  </p>
                </div>

                {/* Botão: redireciona para WhatsApp com mensagem pré-montada */}
                <button
                  type="submit"
                  className="w-full bg-[#B32025] hover:bg-[#8E1A1E] text-white font-semibold py-4 rounded-md transition-all duration-200 whitespace-nowrap cursor-pointer text-sm font-sans flex items-center justify-center gap-2"
                >
                  <i className="ri-whatsapp-line text-base leading-none" />
                  Enviar via WhatsApp
                </button>
              </form>
            )}
          </div>

          {/* Informações de contato */}
          <div className="lg:col-span-2 flex flex-col gap-6">
            {/* WhatsApp direto */}
            <a
              href="https://wa.me/5554996914900?text=Ol%C3%A1%2C%20gostaria%20de%20falar%20com%20um%20advogado."
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-4 lg:gap-5 bg-[#1A1A1A] hover:bg-[#2C2C2C] rounded-lg p-[22px] lg:p-6 transition-all duration-200 cursor-pointer"
            >
              <div className="w-[50px] h-[50px] lg:w-14 lg:h-14 flex items-center justify-center bg-[#B32025]/20 rounded-lg flex-shrink-0">
                <i className="ri-whatsapp-line text-2xl lg:text-3xl text-[#B32025] leading-none" />
              </div>
              <div>
                <p className="text-[#BFBFBF] text-[11px] lg:text-xs font-sans uppercase tracking-widest mb-0.5 lg:mb-1">WhatsApp</p>
                <p className="text-white font-serif font-bold text-lg lg:text-xl">+55 54 99691-4900</p>
                <p className="text-[#BFBFBF] text-[11px] lg:text-xs font-sans mt-0.5 lg:mt-1 group-hover:text-[#B32025] transition-colors">
                  Clique para iniciar conversa →
                </p>
              </div>
            </a>

            {/* Endereço */}
            <div className="flex items-start gap-4 lg:gap-5 bg-white border border-[#E6E6E6] rounded-lg p-[22px] lg:p-6">
              <div className="w-[50px] h-[50px] lg:w-14 lg:h-14 flex items-center justify-center bg-[#B32025]/10 rounded-lg flex-shrink-0">
                <i className="ri-map-pin-line text-xl lg:text-2xl text-[#B32025] leading-none" />
              </div>
              <div>
                <p className="text-[#2C2C2C]/60 text-[11px] lg:text-xs font-sans uppercase tracking-widest mb-0.5 lg:mb-1">Endereço</p>
                <p className="text-[#1A1A1A] font-semibold text-[13px] lg:text-sm font-sans leading-relaxed">
                  Av. Pres. Vargas, 2545 – Sala 11<br />
                  São Cristóvão · Nova Prata – RS<br />
                  CEP: 95320-000
                </p>
              </div>
            </div>

            {/* Instagram */}
            <a
              href="https://www.instagram.com/ivancarlosadvocados/"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="group flex items-center gap-4 lg:gap-5 bg-white border border-[#E6E6E6] hover:border-[#B32025]/30 rounded-lg p-[22px] lg:p-6 transition-all duration-200 cursor-pointer"
            >
              <div className="w-[50px] h-[50px] lg:w-14 lg:h-14 flex items-center justify-center bg-[#B32025]/10 group-hover:bg-[#B32025]/20 rounded-lg flex-shrink-0 transition-colors">
                <i className="ri-instagram-line text-xl lg:text-2xl text-[#B32025] leading-none" />
              </div>
              <div>
                <p className="text-[#2C2C2C]/60 text-[11px] lg:text-xs font-sans uppercase tracking-widest mb-0.5 lg:mb-1">Instagram</p>
                <p className="text-[#1A1A1A] font-semibold text-[13px] lg:text-sm font-sans">@ivancarlosadvocados</p>
                <p className="text-[#BFBFBF] text-[11px] lg:text-xs font-sans mt-0.5 lg:mt-1 group-hover:text-[#B32025] transition-colors">
                  Seguir no Instagram →
                </p>
              </div>
            </a>

            {/* Mapa */}
            <div className="rounded-lg overflow-hidden" style={{ height: "200px" }}>
              <iframe
                title="Localização Ivan Carlos Advogados – Nova Prata RS"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3489.2!2d-51.6100!3d-28.7769!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjjCsDQ2JzM3LjEiUyA1McKwMzYnMzYuMCJX!5e0!3m2!1spt-BR!2sbr!4v1234567890"
                width="100%"
                height="200"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
